









































































































































































































































































































































































































































































































































































































































/* eslint-disable @typescript-eslint/camelcase */
import {
  Component,
  Vue,
  Model,
  Prop,
  Ref,
  Watch,
  InjectReactive
} from 'vue-property-decorator'
import { RelationIds } from '@/shared/Form/components/Mask.vue'

interface TimeEntry {
  date: string
  persons: number | string
  work_hours_per_person: number | string | null
  travel_hours_per_person: number | string | null
  work_hours_total: number
  travel_hours_total: number
}

interface HoursGridRow {
  date: string
  persons: number | string
  hours: number | string | null
  hours_readonly?: string
}

interface ArticlesGridRow {
  date: string
  quantity: number | string | null
  article_readonly?: string
}

interface Article {
  sync_id: number
  input_type: 'time' | 'article'
  line_type: 'title' | 'article' | 'bom_head' | 'bom_line'
  article_name: string
  article_description: string | null
  order_qty: number | null
  delivery_qty: number | null
  hours_grid: HoursGridRow[]
  articles_grid: ArticlesGridRow[]
}

interface RenderableArticle extends Article {
  _idx: number
  _isBomHead: boolean
  _bomParent: number | null
}

@Component
export default class LieferscheinTemplate extends Vue {
  @Model('input', { type: Object, default: () => ({}) })
  readonly value!: Record<string, any>

  @Prop({ type: Boolean }) readonly readonly!: boolean
  @Prop({ type: Boolean }) readonly compact!: boolean
  @Prop({ type: Object }) readonly template!: any

  @InjectReactive() relationIds!: RelationIds
  @InjectReactive() subjectId!: string | null

  @Ref() readonly barSentinel!: HTMLElement
  @Ref() readonly comparisonBar!: Vue

  bomExpanded: Record<number, boolean> = {}
  descriptionDialog = false
  descriptionText = ''

  isBarFixed = false
  barHeight = 48
  private scrollTick = false

  selectedDate = ''

  @Watch('selectedDate')
  onSelectedDateChange(val: string) {
    this.$set(this.values, 'currentDate', val)
  }

  @Watch('value', { immediate: true })
  onValueChange() {
    if (this.value) {
      this.$set(this.value, 'currentDate', this.selectedDate || this.today)
    }
  }

  showOldTimeEntries = false
  showOldAdditionalWorks = false
  showOldGrid: Record<number, boolean> = {}

  created() {
    this.selectedDate = this.today
  }

  mounted() {
    window.addEventListener('scroll', this.onScroll, true)
    this.selectedDate = this.today
  }

  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll, true)
  }

  private onScroll() {
    if (this.scrollTick) return
    this.scrollTick = true
    requestAnimationFrame(() => {
      this.scrollTick = false
      if (!this.barSentinel) return

      const barEl = this.comparisonBar?.$el as HTMLElement | undefined
      if (barEl && !this.isBarFixed) {
        this.barHeight = barEl.offsetHeight
      }

      this.isBarFixed =
        this.barSentinel.getBoundingClientRect().top > window.innerHeight
    })
  }

  get values() {
    return this.value
  }

  set values(v: Record<string, any>) {
    this.$emit('input', v)
  }

  get hasFormWithDates(): boolean {
    return !!this.values.formWithDates
  }

  get formattedSelectedDate(): string {
    if (!this.selectedDate) return ''
    const parts = this.selectedDate.split('-')
    return `${parts[2]}.${parts[1]}.${parts[0]}`
  }

  get today(): string {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(d.getDate()).padStart(2, '0')}`
  }

  // --- Date filtering ---

  isCurrentDate(date: string | undefined): boolean {
    if (!date) return true
    return date === this.selectedDate
  }

  formatDate(date: string): string {
    if (!date) return ''
    const parts = date.split('-')
    return `${parts[2]}.${parts[1]}.${parts[0]}`
  }

  isFirstOfDate(grid: { date: string }[], index: number): boolean {
    if (index === 0) return true
    return grid[index].date !== grid[index - 1].date
  }

  isTimeEntryCurrent(entry: TimeEntry): boolean {
    if (!entry.date) return false
    return entry.date === this.selectedDate
  }

  get oldTimeEntriesCount(): number {
    return this.timeEntries.filter(e => !this.isTimeEntryCurrent(e)).length
  }

  oldHoursGridCount(articleIdx: number): number {
    const grid = this.articles[articleIdx]?.hours_grid
    if (!grid) return 0
    return grid.filter(r => !this.isCurrentDate(r.date)).length
  }

  oldArticlesGridCount(articleIdx: number): number {
    const grid = this.articles[articleIdx]?.articles_grid
    if (!grid) return 0
    return grid.filter(r => !this.isCurrentDate(r.date)).length
  }

  // --- Data accessors ---

  get timeEntries(): TimeEntry[] {
    if (!this.values.time_entries) {
      this.$set(this.values, 'time_entries', [])
    }
    if (this.hasFormWithDates) {
      for (const entry of this.values.time_entries) {
        if (!entry.date) {
          this.$set(entry, 'date', '2026-01-01')
        }
      }
    }
    return this.values.time_entries
  }

  get articles(): Article[] {
    return this.values.articles || []
  }

  get renderableArticles(): RenderableArticle[] {
    let currentBomHead: number | null = null

    return this.articles.map((article, idx) => {
      if (article.line_type === 'bom_head') {
        currentBomHead = idx
      } else if (article.line_type !== 'bom_line') {
        currentBomHead = null
      }

      return {
        ...article,
        _idx: idx,
        _isBomHead: article.line_type === 'bom_head',
        _bomParent: article.line_type === 'bom_line' ? currentBomHead : null
      }
    })
  }

  // --- Helpers ---

  toNum(v: any): number {
    return parseFloat(v) || 0
  }

  showDescription(text: string) {
    this.descriptionText = text
    this.descriptionDialog = true
  }

  // --- Time entry computations ---

  workTotal(entry: TimeEntry): number {
    return (
      Math.round(
        this.toNum(entry.work_hours_per_person) *
          this.toNum(entry.persons) *
          100
      ) / 100
    )
  }

  travelTotal(entry: TimeEntry): number {
    return (
      Math.round(
        this.toNum(entry.travel_hours_per_person) *
          this.toNum(entry.persons) *
          100
      ) / 100
    )
  }

  get currentDayTimeEntries(): TimeEntry[] {
    return this.timeEntries.filter(e => this.isTimeEntryCurrent(e))
  }

  get totalWorkHours(): number {
    return (
      Math.round(
        this.currentDayTimeEntries.reduce(
          (sum, e) => sum + this.workTotal(e),
          0
        ) * 100
      ) / 100
    )
  }

  get totalTravelHours(): number {
    return (
      Math.round(
        this.currentDayTimeEntries.reduce(
          (sum, e) => sum + this.travelTotal(e),
          0
        ) * 100
      ) / 100
    )
  }

  get netWorkHours(): number {
    return Math.round((this.totalWorkHours - this.totalTravelHours) * 100) / 100
  }

  // --- Hours grid computations ---

  get totalAllocatedHours(): number {
    let sum = 0
    for (const article of this.articles) {
      if (article.input_type === 'time' && article.hours_grid) {
        for (const row of article.hours_grid) {
          if (this.isCurrentDate(row.date)) {
            sum += this.toNum(row.hours) * this.toNum(row.persons)
          }
        }
      }
    }
    sum += this.currentDayAdditionalWorkHours
    return Math.round(sum * 100) / 100
  }

  get hoursDiff(): number {
    return (
      Math.round((this.netWorkHours - this.totalAllocatedHours) * 100) / 100
    )
  }

  deliveredHours(article: Article): number {
    if (!article.hours_grid) return 0
    return (
      Math.round(
        article.hours_grid.reduce(
          (sum, r) => sum + this.toNum(r.hours) * this.toNum(r.persons),
          0
        ) * 100
      ) / 100
    )
  }

  deliveredQty(article: Article): number {
    if (!article.articles_grid) return 0
    return (
      Math.round(
        article.articles_grid.reduce(
          (sum, r) => sum + this.toNum(r.quantity),
          0
        ) * 100
      ) / 100
    )
  }

  // --- Mutation methods ---

  addTimeEntry() {
    this.timeEntries.push({
      date: this.selectedDate,
      persons: 1,
      work_hours_per_person: null,
      travel_hours_per_person: null,
      work_hours_total: 0,
      travel_hours_total: 0
    })
  }

  addHoursRow(item: RenderableArticle) {
    const article = this.articles[item._idx]
    if (!article.hours_grid) {
      this.$set(article, 'hours_grid', [])
    }
    article.hours_grid.push({
      date: this.selectedDate,
      persons: 1,
      hours: null,
      hours_readonly: 'false'
    })
  }

  addArticleRow(item: RenderableArticle) {
    const article = this.articles[item._idx]
    if (!article.articles_grid) {
      this.$set(article, 'articles_grid', [])
    }
    article.articles_grid.push({
      date: this.selectedDate,
      quantity: null,
      article_readonly: 'false'
    })
  }

  // --- Additional works ---

  get additionalWorks(): {
    date: string
    description: string
    persons: number | string
    hours: number | string | null
    sync_id: number | null
  }[] {
    if (!this.values.additional_works) {
      this.$set(this.values, 'additional_works', [])
    }
    return this.values.additional_works
  }

  get oldAdditionalWorksCount(): number {
    return this.additionalWorks.filter(w => !this.isCurrentDate(w.date)).length
  }

  additionalWorkTotal(work: {
    persons: number | string
    hours: number | string | null
  }): number {
    return (
      Math.round(this.toNum(work.hours) * this.toNum(work.persons) * 100) / 100
    )
  }

  get currentDayAdditionalWorkHours(): number {
    return (
      Math.round(
        this.additionalWorks
          .filter(w => this.isCurrentDate(w.date))
          .reduce((sum, w) => sum + this.additionalWorkTotal(w), 0) * 100
      ) / 100
    )
  }

  addAdditionalWork() {
    this.additionalWorks.push({
      date: this.selectedDate,
      description: '',
      persons: 1,
      hours: null,
      sync_id: null
    })
  }
}
